/**
 * ConfirmDialog/confirm.ts
 * desc: the imperative side of the confirmation dialog — confirm() opens the question and resolves with the answer;
 *       the host (index.tsx) renders whatever question is open
 * note: a module-level store rather than a redux slice, so any code can ask without importing the store (which would
 *       re-create the store → utilities import cycle). One question at a time: a new one cancels the one still open
 */

import type { ReactNode } from 'react';

export interface ConfirmOptions {
    // the control the question is shown beside; without it the question is centered over a dimmed page
    anchor?: HTMLElement;
    cancelLabel?: string;
    confirmLabel?: string;
    text: ReactNode;
}

export interface ConfirmPrompt extends ConfirmOptions {
    // where focus goes when the question closes, or null when it should stay where the browser leaves it
    returnFocus: HTMLElement | null;
    resolve: (isConfirmed: boolean) => void;
}

let current: ConfirmPrompt | null = null;
const listeners = new Set<() => void>();

const _emit = () => { listeners.forEach((listener) => { listener(); }); };

// the anchor, else whatever had focus — but only when the user got there by keyboard. A mouse user never sees
// where focus lands, and focusing an icon opens its tooltip (radix opens on any focus, see primitives#1800)
const _getReturnFocus = (anchor?: HTMLElement) => {
    const target = anchor ?? document.activeElement;
    return target instanceof HTMLElement && target.matches(':focus-visible') ? target : null;
};

export const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
};

export const getSnapshot = () => current;

// answers the open question; a no-op when none is open
export const settle = (isConfirmed: boolean) => {
    const prompt = current;
    if (!prompt) return;
    current = null;
    _emit();
    prompt.resolve(isConfirmed);
};

export const confirm = (options: ConfirmOptions) => new Promise<boolean>((resolve) => {
    // a question replacing an open one keeps its focus target — the open one's button still has focus right now
    const returnFocus = current ? current.returnFocus : _getReturnFocus(options.anchor);
    settle(false);
    current = { ...options, returnFocus, resolve };
    _emit();
});
